import type { FC } from 'react';
import {
  Boxes,
  Cpu,
  Database,
  Radio,
  Server,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const HomePage: FC = () => {
  return (
    <div className="space-y-8">
      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900/90 to-brand-950/40 border border-slate-800 p-8 md:p-10 shadow-2xl">
        <div className="absolute -right-16 -top-16 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-semibold mb-4">
            <Zap className="w-3.5 h-3.5" />
            <span>Developed by Axiino — Sovereign Mail Engine</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-4 leading-tight">
            keerav camp Email Infrastructure
          </h1>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-6">
            Autonomous, sovereign email infrastructure and campaign automation. Built from the ground up with clean-room architecture, independent of third-party mail APIs.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/health"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-medium text-sm transition-all shadow-lg shadow-brand-600/25"
            >
              <Radio className="w-4 h-4 animate-pulse" />
              Check Cluster Diagnostics
            </Link>
          </div>
        </div>
      </div>

      {/* Monorepo Architecture Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4 border border-blue-500/20">
            <Server className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">NestJS API Core</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            Modular REST gateway providing strict DTO validation, RFC exception filtering, URI versioning, and Prisma database connections.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/api (:4000)
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center mb-4 border border-amber-500/20">
            <Cpu className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">BullMQ Worker Pool</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            Distributed background execution engine configured with Redis for asynchronous priority queuing and task processing.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/worker (Node.js)
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-4 border border-emerald-500/20">
            <Database className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">Prisma & PostgreSQL</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            PostgreSQL 16 relational core managed with Prisma migrations and connection health validation.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/api/prisma
          </div>
        </div>
      </div>

      {/* Package Ecosystem */}
      <div className="p-6 rounded-2xl bg-slate-900/30 border border-slate-800/80">
        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Boxes className="w-4 h-4 text-brand-400" />
          <span>Shared Internal Packages</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
            <p className="text-sm font-semibold text-white">@antigravity/types</p>
            <p className="text-xs text-slate-400 mt-1">
              Shared TypeScript contracts, API interfaces, and domain models.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
            <p className="text-sm font-semibold text-white">@antigravity/config</p>
            <p className="text-xs text-slate-400 mt-1">
              Centralized constants, queues, ports, and configuration standards.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
            <p className="text-sm font-semibold text-white">@antigravity/shared</p>
            <p className="text-xs text-slate-400 mt-1">
              Utility helpers, response builders, email validators, and crypto tools.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
